/**
 * /security/setup: a staff member or clinic member adds an authenticator app. The page asks the
 * Worker for a new secret, shows it as a QR code and as text, lists the ten backup codes once, and
 * confirms with the app's first code. Confirming also passes the second factor for this session,
 * so the person goes straight on to where they were headed.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import { renderSVG } from "uqr";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { Checkbox } from "@lattice/ui/Checkbox";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { visuallyHidden } from "@lattice/ui/visuallyHidden";
import type { TotpStart } from "../api.ts";
import { confirmTotp, loadSecurity, startTotp } from "../api.ts";
import { AuthLayout, authStyles, nextPath, signInAgainPath, TryAgain } from "../AuthLayout.tsx";
import { CodeForm } from "./CodeForm.tsx";
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "enrol";
  readonly start: TotpStart;
};
function backupFile(codes: readonly string[]): string {
  return `Lattice backup codes\nEach works once if you lose your phone.\n\n${codes.join("\n")}\n`;
}
function downloadCodes(codes: readonly string[]): void {
  const url = URL.createObjectURL(new Blob([backupFile(codes)], {
    type: "text/plain"
  }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "lattice-backup-codes.txt";
  link.click();
  /* Revoked later, because Safari can drop a download whose address is gone by the next tick. */
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
const COPIED_MS = 2000;
function CopyText({
  value,
  label,
  copied,
  failed,
  testId
}: {
  readonly value: string;
  readonly label: string;
  /** What a screen reader hears once it is copied. */
  readonly copied: string;
  readonly failed: string;
  readonly testId: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state !== "copied") return;
    const timer = window.setTimeout(() => setState("idle"), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [state]);
  return <>
      <Button tone="quiet" size="small" testId={testId} onClick={() => {
      navigator.clipboard.writeText(value).then(() => setState("copied"), () => setState("failed"));
    }}>
        {state === "copied" ? "Copied" : label}
      </Button>
      <span role="status" {...stylex.props(visuallyHidden.hidden)}>
        {state === "copied" ? copied : ""}
      </span>
      {state === "failed" ? <p role="alert" {...stylex.props(authStyles.error)}>
          {failed}
        </p> : null}
    </>;
}
function CopyCodes({
  codes
}: {
  readonly codes: readonly string[];
}) {
  return <CopyText value={codes.join("\n")} label="Copy codes" copied="Backup codes copied" failed="Could not copy. Select the codes and copy them yourself." testId="copy-backup-codes" />;
}
function leave(): void {
  window.location.assign(nextPath() ?? "/");
}
function verifyInstead(): void {
  const next = nextPath();
  window.location.assign(next === null ? "/security/verify" : `/security/verify?next=${encodeURIComponent(next)}`);
}
async function begin(): Promise<Screen> {
  const status = await loadSecurity();
  if (status === "signed_out") return {
    kind: "signed_out"
  };
  if (status === null) return {
    kind: "failed"
  };
  if (status.passed) {
    leave();
    return {
      kind: "loading"
    };
  }
  if (status.totp || status.passkeys > 0) {
    verifyInstead();
    return {
      kind: "loading"
    };
  }
  const started = await startTotp();
  return started.status === "ok" ? {
    kind: "enrol",
    start: started.start
  } : {
    kind: "failed"
  };
}

/*
  Each start writes a new secret, so it must run once per page load. React runs effects twice in
  development, and a second start would replace the secret the page is already showing.
*/
let started: Promise<Screen> | null = null;
export function SecuritySetup() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    document.title = "Set up sign-in · Lattice";
    started ??= begin().catch((): Screen => ({
      kind: "failed"
    }));
    void started.then(setScreen);
  }, []);
  if (screen.kind === "loading") return <AuthLayout title="Set up a second step" lead="Getting your authenticator code ready.">
        <Pending message="Preparing" bones={["title", "line", "control"]} />
      </AuthLayout>;
  if (screen.kind === "signed_out") return <AuthLayout title="Your sign-in has ended" lead="Sign in again with your email, then you can set up your authenticator." focusHeading>
        <LinkButton href={signInAgainPath()}>Sign in again</LinkButton>
      </AuthLayout>;
  if (screen.kind === "failed") return <AuthLayout title="We couldn't start setup" lead="Try again in a minute. If it keeps happening, write to support." focusHeading>
        <TryAgain />
      </AuthLayout>;
  return <AuthLayout title="Set up a second step" lead="Staff and clinic accounts sign in with an email code and a code from an authenticator app, such as 1Password, Google Authenticator or Authy.">
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh xqu410z">1. Scan this code with your app</h2>
        <div className="xqcrz7y xhpatk0 x1a91ssu x1fbwzx xcqm4px xmkeg23 x1y0btm7 xtkjqb" role="img" aria-label="QR code for your authenticator app" data-testid="totp-qr" dangerouslySetInnerHTML={{
        __html: renderSVG(screen.start.otpauthUrl)
      }} />
        <p {...stylex.props(authStyles.body)}>Or enter this key in the app:</p>
        <p className="xrvj5dj xueod8l x1vvqtvb xcfm6fe x1ghz6dp x1bc1bhi x1rf3zrh xj0a0fe xzb3lse" data-testid="totp-secret">
          {(screen.start.secret.match(/.{1,4}/g) ?? []).map((group, at) => <span key={at} className="x1rg5ohu">
              {group}
            </span>)}
        </p>
        <CopyText value={screen.start.secret} label="Copy key" copied="Key copied" failed="Could not copy. Select the key and copy it yourself." testId="copy-totp-secret" />
      </div>
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh xqu410z">2. Save your backup codes</h2>
        <p {...stylex.props(authStyles.body)}>
          Each works once if you lose your phone. They are shown only now, so copy or download them
          before you turn this on.
        </p>
        <ol className="x1ghz6dp x1ua174w xrvj5dj xnby9oq xskm3c9 xl4dl4e xss6m8b x1qfunw5" data-testid="backup-codes">
          {screen.start.backupCodes.map(code => <li key={code}>{code}</li>)}
        </ol>
        <div className="x78zum5 x1a02dak x6s0dn4 x626il7">
          <CopyCodes codes={screen.start.backupCodes} />
          <Button tone="quiet" size="small" testId="download-backup-codes" onClick={() => downloadCodes(screen.start.backupCodes)}>
            Download as a text file
          </Button>
        </div>
        <Checkbox label="I have saved these backup codes" checked={saved} onChange={setSaved} testId="backup-codes-saved" />
      </div>
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh xqu410z">3. Enter the code from your app</h2>
        <CodeForm submitLabel="Turn on authenticator" check={confirmTotp} onPassed={leave} {...saved ? {} : {
        disabledReason: "Save your backup codes first"
      }} />
      </div>
    </AuthLayout>;
}